import React from 'react'
import { Container, Button, Grid, Box, Link, useMediaQuery, Typography } from '@material-ui/core'
import { TitleH3Bold } from '../../theme/typography'
import PropTypes from 'proptypes'
import styled from 'styled-components'
import { GatsbyImage } from 'gatsby-plugin-image'
import { placeImage } from '../../utils'

const ImageRight = styled(GatsbyImage)(({ maxWidth, right, left, marginBottom, width }) => ({
  position: 'absolute',
  maxWidth: maxWidth ? maxWidth : '50%',
  bottom: -1,
  right: right ? right : 0,
  left: left ? left : 'auto',
  display: 'block',
  width: width ? width : 'auto',
  '@media (max-width: 959px)': {
    position: 'relative',
    width: ' 69%',
    maxWidth: 350,
    margin: 'auto',
    marginBottom: marginBottom ? marginBottom : 'auto',
    zIndex: 1
  }
}))
const ShadowBox = styled(Grid)(({ theme, reverse }) => ({
  boxShadow: theme.shadows[7],
  borderRadius: 8,
  position: 'relative',
  backgroundColor: theme.colors.white,
  flexDirection: reverse ? 'row-reverse' : 'row'
}))
const TextGrid = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(4, 2),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(5, 2, 5, 5.5)
  }
}))
const ButtonBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginLeft: -theme.spacing(1),
  marginRight: -theme.spacing(1),
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  '& a': {
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    minWidth: '8rem',
    margin: theme.spacing(1, 1, 0)
  },
  '@media (max-width: 959px)': {
    marginTop: theme.spacing(1),
    justifyContent: 'center'
  }
}))
const WrapBox = styled(Box)(({ theme, colortop, colorbottom }) => ({
  backgroundImage: `linear-gradient(to bottom, ${colortop} 50%, ${colorbottom} 50%, ${colorbottom})`,
  paddingTop: theme.spacing(10),
  paddingBottom: theme.spacing(2),
  position: 'relative',
  zIndex: 1,
  '@media (max-width: 959px)': {
    textAlign: 'center',
    paddingTop: 0
  }
}))
const TitleH2BoldMenuStyled = styled(TitleH3Bold)({
  marginBottom: '0.3rem',
  '@media (max-width: 959px)': {
    fontSize: '1.9rem'
  }
})
const TitleMediumStyled = styled(Typography)({
  fontSize: '1.25rem !important',
  marginTop: '10px !important',
  marginBottom: '16px !important',
  '@media (max-width: 959px)': {
    fontSize: '1rem !important'
  }
})
const ContainerStyled = styled(Container)(({ containerwidth }) => ({
  maxWidth: containerwidth ? containerwidth : 1248
}))

function FloatingBlock(props) {
  const mobile = useMediaQuery('(max-width: 959px)')
  return (
    <WrapBox pb={1} colortop={props.colortop} colorbottom={props.colorbottom}>
      <ContainerStyled maxWidth={props.small ? 'md' : 'lg'} containerwidth={props.containerWidth}>
        {mobile && props.footerImage && (
          <ImageRight marginBottom={props.marginBottom} image={props.footerImage.gatsbyImageData} />
        )}

        <ShadowBox container reverse={props.reverse}>
          <TextGrid item xs={12} md={props.maxWidth > '50%' ? 5 : 7}>
            <TitleH2BoldMenuStyled>{props.title}</TitleH2BoldMenuStyled>
            {props.subtitle && (
              <TitleMediumStyled variant="h3" color="textSecondary" gutterBottom>
                {props.subtitle}
              </TitleMediumStyled>
            )}
            {props.text && (
              <Typography variant="body1" color="textSecondary" gutterBottom>
                {props.text}
              </Typography>
            )}
            {props.ctas && (
              <ButtonBox>
                {props.ctas.map((cta, i) => {
                  return cta.image ? (
                    <Link
                      key={`block-link${i}`}
                      href={cta.href}
                      target={cta.openIn}
                      rel={cta.rel}
                      title={cta.title}
                      data-app-store={cta.appStore}
                    >
                      {' '}
                      {placeImage(cta.image)}
                    </Link>
                  ) : props.ctaInverted ? (
                    <Button
                      key={`block-button${i}`}
                      variant="contained"
                      color={i > 0 ? 'secondary' : 'primary'}
                      href={cta.href}
                      target={cta.openIn}
                      rel={cta.rel}
                      title={cta.title}
                      data-ads-event={cta.adsEvent}
                    >
                      {cta.text}
                    </Button>
                  ) : (
                    <Button
                      key={`block-button${i}`}
                      variant="contained"
                      color={i > 0 ? 'primary' : 'secondary'}
                      href={cta.href}
                      target={cta.openIn}
                      rel={cta.rel}
                      title={cta.title}
                      data-ads-event={cta.adsEvent}
                    >
                      {cta.text}
                    </Button>
                  )
                })}
              </ButtonBox>
            )}
          </TextGrid>
          <Grid item xs={12} md={props.maxWidth > '50%' ? 7 : 5}>
            {!mobile && props.footerImage && (
              <ImageRight
                maxWidth={props.maxWidth}
                right={props.right}
                left={props.left}
                image={props.footerImage.gatsbyImageData}
                alt={props.imageAlt}
                width={props.width}
              />
            )}
          </Grid>
        </ShadowBox>
      </ContainerStyled>
    </WrapBox>
  )
}

export default FloatingBlock

FloatingBlock.propTypes = {
  colortop: PropTypes.string,
  colorbottom: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  image: PropTypes.string,
  image2x: PropTypes.string,
  image3x: PropTypes.string,
  ctas: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string,
      text: PropTypes.string
    })
  )
}

FloatingBlock.defaultProps = {
  ctas: []
}
